const datePattern = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Creates error.
 *
 * @param {Array<*>} errors - Mutable validation errors keyed by field name.
 * @param {string} field - Field associated with the operation.
 * @param {string} message - User-presentable message.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function addError(errors, field, message) {
  if (!errors[field]) errors[field] = message;
}

/**
 * Determines whether blank.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
export function isBlank(value) {
  return String(value || '').trim().length === 0;
}

/**
 * Performs the required string operation.
 *
 * @param {Array<*>} errors - Mutable validation errors keyed by field name.
 * @param {string} field - Field associated with the operation.
 * @param {*} value - Value to inspect or transform.
 * @param {*} label - label value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function requiredString(errors, field, value, label) {
  if (isBlank(value)) addError(errors, field, `${label} is required.`);
}

/**
 * Performs the max length operation.
 *
 * @param {Array<*>} errors - Mutable validation errors keyed by field name.
 * @param {string} field - Field associated with the operation.
 * @param {*} value - Value to inspect or transform.
 * @param {*} max - max value.
 * @param {*} label - label value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function maxLength(errors, field, value, max, label) {
  if (value != null && String(value).length > max) {
    addError(errors, field, `${label} must be ${max} characters or fewer.`);
  }
}

/**
 * Performs the optional url operation.
 *
 * @param {Array<*>} errors - Mutable validation errors keyed by field name.
 * @param {string} field - Field associated with the operation.
 * @param {*} value - Value to inspect or transform.
 * @param {*} label - label value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function optionalUrl(errors, field, value, label) {
  if (isBlank(value)) return;
  try {
    const url = new URL(value);
    if (!['http:', 'https:'].includes(url.protocol)) {
      addError(errors, field, `${label} must be a valid web URL.`);
    }
  } catch {
    addError(errors, field, `${label} must be a valid web URL.`);
  }
}

/**
 * Performs the optional date operation.
 *
 * @param {Array<*>} errors - Mutable validation errors keyed by field name.
 * @param {string} field - Field associated with the operation.
 * @param {*} value - Value to inspect or transform.
 * @param {*} label - label value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function optionalDate(errors, field, value, label) {
  if (isBlank(value)) return;
  if (!datePattern.test(String(value))) {
    addError(errors, field, `${label} must use YYYY-MM-DD format.`);
  }
}

/**
 * Performs the date order operation.
 *
 * @param {Array<*>} errors - Mutable validation errors keyed by field name.
 * @param {*} startValue - start value value.
 * @param {string} endField - end field value.
 * @param {*} endValue - end value value.
 * @param {string} message - User-presentable message.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function dateOrder(errors, startValue, endField, endValue, message) {
  if (isBlank(startValue) || isBlank(endValue)) return;
  if (datePattern.test(String(startValue)) && datePattern.test(String(endValue)) && startValue > endValue) {
    addError(errors, endField, message || 'End date must be after start date.');
  }
}

/**
 * Performs the number range operation.
 *
 * @param {Array<*>} errors - Mutable validation errors keyed by field name.
 * @param {string} field - Field associated with the operation.
 * @param {*} value - Value to inspect or transform.
 * @param {object} options4 - options4 value.
 * @param {*} options4.min - min value.
 * @param {*} options4.max - max value.
 * @param {*} options4.label - label value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function numberRange(errors, field, value, { min, max, label }) {
  if (value === '' || value == null) return;
  const number = Number(value);
  if (!Number.isFinite(number)) {
    addError(errors, field, `${label} must be a number.`);
    return;
  }
  if (min != null && number < min) addError(errors, field, `${label} must be at least ${min}.`);
  if (max != null && number > max) addError(errors, field, `${label} must be ${max} or less.`);
}

/**
 * Performs the one of operation.
 *
 * @param {Array<*>} errors - Mutable validation errors keyed by field name.
 * @param {string} field - Field associated with the operation.
 * @param {*} value - Value to inspect or transform.
 * @param {*} allowed - allowed value.
 * @param {*} label - label value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function oneOf(errors, field, value, allowed, label) {
  if (isBlank(value)) return;
  if (!allowed.includes(value)) addError(errors, field, `${label} is not supported.`);
}

/**
 * Performs the array max operation.
 *
 * @param {Array<*>} errors - Mutable validation errors keyed by field name.
 * @param {string} field - Field associated with the operation.
 * @param {*} value - Value to inspect or transform.
 * @param {*} max - max value.
 * @param {*} label - label value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function arrayMax(errors, field, value, max, label) {
  if (Array.isArray(value) && value.length > max) {
    addError(errors, field, `${label} supports up to ${max} items.`);
  }
}

/**
 * Performs the result operation.
 *
 * @param {Array<*>} fieldErrors - Validation errors keyed by field name.
 * @returns {*} The derived operation result.
 */
export function result(fieldErrors) {
  return {
    valid: Object.keys(fieldErrors).length === 0,
    fieldErrors,
    formError: Object.keys(fieldErrors).length ? 'Please fix the highlighted fields.' : '',
  };
}
